import api from './axiosConfig';

export interface ApprovalAttachment {
    id: string;
    request_id: string;
    filename: string;
    file_path: string;
    file_type: string;
    document_type: string;
    uploaded_by: string;
    uploaded_at: string;
}

export interface ApprovalOccupant {
    name: string;
    surname: string;
    personal_id: string;
    employer_name?: string | null;
    monthly_income?: string | null;
    cell_number?: string | null;
    id: string;
    request_id: string;
    photo_path?: string;
}

export interface ApprovalItem {
    id: string;
    indigent_number: string;
    account_number: string;
    sa_id: string;
    name: string;
    surname: string;
    employer_name?: string | null;
    source_of_income?: string | null;
    monthly_income?: number | null;
    cell_number?: string | null;
    email?: string | null;
    erf_no?: string | null;
    ward_no?: string | null;
    address_line_1?: string | null;
    address_line_2?: string | null;
    address_line_3?: string | null;
    town?: string | null;
    electricity_meter_no?: string | null;
    legacy_application_number?: string | null;
    service_electricity?: boolean;
    service_water?: boolean;
    service_waste?: boolean;
    service_waste_water?: boolean;
    status: 'pending' | 'reviewed' | 'approved' | 'failed' | 'rejected';
    financial_year: number;
    declaration_accepted?: boolean;
    submitted_by: string;
    updated_by?: string | null;
    submitted_by_username?: string | null;
    created_at: string;
    updated_at: string;
    approved_by?: string | null;
    approved_at?: string | null;
    rejected_by?: string | null;
    rejected_at?: string | null;
    review_remark?: string | null;
    // Add username fields for reviewer information
    approved_by_username?: string | null;
    rejected_by_username?: string | null;
    review_passed_by_username?: string | null;
    review_failed_by_username?: string | null;
    review_passed_at?: string | null;
    review_failed_at?: string | null;
    occupants: ApprovalOccupant[];
    attachments: ApprovalAttachment[];
}

export interface ApprovalResponse {
    data: ApprovalItem[];
    total: number;
    page: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
}

export interface ApprovalFilters {
    status?: 'pending' | 'reviewed' | 'approved' | 'failed' | 'rejected';
    electricity_meter_no?: string;
    username?: string;
    account_number?: string;
    sa_id?: string;
    indigent_number?: string;
    limit?: number;
    page?: number;
}

export interface ExportApprovalParams {
    ward_no?: string;
    approved?: boolean;
    review_passed?: boolean;
    financial_year?: string;
    format: 'csv' | 'xls' | 'pdf';
}

export const approvalService = {
    getApprovals: async (filters: ApprovalFilters = {}): Promise<ApprovalResponse> => {
        try {
            const params = new URLSearchParams();

            // Add filters to query parameters
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });

            const response = await api.get<ApprovalResponse>(`/approvals/?${params.toString()}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    exportApprovals: async (params: ExportApprovalParams): Promise<void> => {
        try {
            const queryParams = new URLSearchParams();

            // Add export parameters to query string
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value.toString());
                }
            });

            const response = await api.get(`/approvals/export?${queryParams.toString()}`, {
                responseType: 'blob', // Important for file download
            });

            // Create a blob from the response data
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/octet-stream',
            });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Set filename based on format
            const timestamp = new Date().toISOString().split('T')[0];
            // Backend returns real .xlsx for excel/xls formats
            const ext = params.format.replace(/^(excel|xls)$/, 'xlsx');
            const filename = `approvals_export_${timestamp}.${ext}`;
            link.download = filename;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            // Check if this is an AxiosError with response data
            if (error.response && error.response.data) {
                // For blob responses, we need to read the data as text to get the error message
                if (error.response.data instanceof Blob) {
                    const text = await error.response.data.text();
                    try {
                        const errorData = JSON.parse(text);
                        if (errorData.detail && errorData.detail.includes('No records found for export')) {
                            throw new Error('No records found for export');
                        }
                    } catch (parseError) {
                        // If parsing fails, throw the original error
                        throw error;
                    }
                } else {
                    // For regular JSON responses
                    if (error.response.data.detail && error.response.data.detail.includes('No records found for export')) {
                        throw new Error('No records found for export');
                    }
                }
            }
            throw error;
        }
    },

    updateApprovalStatus: async (
        requestId: string,
        status: 'reviewed' | 'failed' | 'approved' | 'rejected',
        remark?: string,
        file?: File,
    ): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append('status', status);

            if (remark && remark.trim()) {
                formData.append('note', remark.trim());
            }

            if (file) {
                formData.append('files', file);
            }

            const response = await api.put(`/approvals/${requestId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Helper method to map API status to UI status
    mapStatusToUI: (
        apiStatus: string,
    ): 'Pending' | 'Approved' | 'Rejected' | 'Review Passed' | 'Review Failed' => {
        switch (apiStatus) {
            case 'pending':
                return 'Pending';
            case 'reviewed':
                return 'Review Passed';
            case 'approved':
                return 'Approved';
            case 'failed':
                return 'Review Failed';
            case 'rejected':
                return 'Rejected';
            default:
                return 'Pending';
        }
    },

    // Helper method to map UI status to API status
    mapUIToAPIStatus: (uiStatus: string): string => {
        switch (uiStatus) {
            case 'Pending':
                return 'pending';
            case 'Review Passed':
                return 'reviewed';
            case 'Approved':
                return 'approved';
            case 'Review Failed':
                return 'failed';
            case 'Rejected':
                return 'rejected';
            default:
                return 'pending';
        }
    },
};
